import {
  ComponentType,
  createContext,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useProject } from 'store/project';
import { singleInspectionReportOnViewToModel } from 'shared/domain/report/mapping/toModel';
import { ReportType } from 'shared/domain/report/types/model/common';
import { SingleInspectionReportModel } from 'shared/domain/report/types/model/singleInspectionReportModel';
import { useInspection } from 'store/inspection';
import { CreateReportDialog } from 'views/reports/wizard/dialog';
import { ContentSelection } from '../contentSelection';
import { ReportAvailabilityShell } from '../contexts/ReportAvailabilityShell';
import { ReportFormEstimateShell } from '../contexts/ReportPageEstimateContext';
import { useReportDialogBase } from '../hooks/useReportDialogBase';
import {
  saveInspectionReportValues,
  useInspectionReportInitialValues,
} from '../hooks/useReportInitialValues';
import {
  handleInspectionReportFormValidation,
  validateInspectionReport,
} from '../reportValidation';
import {
  CreateSingleInspectionReportContext,
  SingleInspectionReportOnView,
} from './types';

const createSingleInspectionReportContext = createContext<
  CreateSingleInspectionReportContext | undefined
>(undefined);

const WithCreateSingleInspectionReport: FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const {
    open,
    isPosting,
    openDialog,
    closeDialog,
    releaseSubmitEvent,
    submitReport,
    SUBMIT_EVENT_TYPE,
  } = useReportDialogBase<SingleInspectionReportModel>(
    'submit-single-inspection-report'
  );

  const { inspection } = useInspection();
  const project = useProject();
  const inspectionId = inspection?._id;

  const submitForm = useCallback(
    async (values: SingleInspectionReportOnView) => {
      if (!inspectionId) return;

      const reportModel = singleInspectionReportOnViewToModel({
        ...values,
        inspection: inspectionId,
      });

      return submitReport({
        reportModel,
        onBeforeSubmit: () => {
          saveInspectionReportValues(project._id, values);
        },
      });
    },
    [inspectionId, project._id, submitReport]
  );

  const initialValues = useInspectionReportInitialValues([open]);

  const contentSelection = useCallback(
    () => <ContentSelection reportType={ReportType.inspection} />,
    []
  );

  const wrapForm = useCallback(
    (form: ReactNode) => (
      <ReportAvailabilityShell reportType={ReportType.inspection} open={open}>
        <ReportFormEstimateShell open={open} kind={ReportType.inspection}>
          {form}
        </ReportFormEstimateShell>
      </ReportAvailabilityShell>
    ),
    [open]
  );

  const ctx: CreateSingleInspectionReportContext = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog,
      submitForm,
      isPosting,
      initialValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate: validateInspectionReport,
      handleFormValidation: handleInspectionReportFormValidation,
      ContentSelection: contentSelection,
      issueParams: null,
      reportEstimateKind: ReportType.inspection,
      wrapForm,
    };
  }, [
    open,
    openDialog,
    closeDialog,
    submitForm,
    isPosting,
    initialValues,
    SUBMIT_EVENT_TYPE,
    releaseSubmitEvent,
    contentSelection,
    wrapForm,
  ]);

  return (
    <createSingleInspectionReportContext.Provider value={ctx}>
      {children}
      <CreateReportDialog
        useReportContext={useCreateSingleInspectionReport}
        titleId='issue_list_send_report_action'
      />
    </createSingleInspectionReportContext.Provider>
  );
};

function useCreateSingleInspectionReport(): CreateSingleInspectionReportContext {
  const context = useContext(createSingleInspectionReportContext);
  if (context === undefined) {
    throw new Error(
      'useCreateSingleInspectionReport must be used within an CreateSingleInspectionReportContext'
    );
  }
  return context;
}

const withCreateSingleInspectionReport =
  (Component: ComponentType) =>
  ({ ...props }): ReactElement => (
    <WithCreateSingleInspectionReport>
      <Component {...props} />
    </WithCreateSingleInspectionReport>
  );

export {
  WithCreateSingleInspectionReport,
  useCreateSingleInspectionReport,
  withCreateSingleInspectionReport,
};
export type { SingleInspectionReportOnView };
